import * as jsforce from 'jsforce'
import { STANDARD_SOBJECT_NAMES } from '../../constants/salesforce'

// PDFをSalesforce Filesに保存（バージョン管理対応）
export async function savePDFToSalesforceFiles(
  conn: jsforce.Connection,
  prSheetId: string,
  pdfBlob: Blob,
  fileName: string,
  existingContentDocumentId?: string | null,
): Promise<string> {
  // BlobをBase64に変換
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const dataUrl = reader.result as string
      // data:application/pdf;base64,<base64data> の形式から base64部分のみ抽出
      const base64 = dataUrl.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(pdfBlob)
  })

  // ContentVersionレコード作成
  const contentVersionData: {
    Title: string
    PathOnClient: string
    VersionData: string
    ContentDocumentId?: string
    FirstPublishLocationId?: string
  } = {
    Title: fileName,
    PathOnClient: fileName,
    VersionData: base64Data,
  }

  // 既存のContentDocumentIdがある場合は新しいバージョンとして追加
  if (existingContentDocumentId) {
    contentVersionData.ContentDocumentId = existingContentDocumentId

    // 既存のContentDocumentLinkを事前に確認・修正
    // ShareType = "I" (Inferred) にすることで「Set by Record」を実現
    const existingLinks = await conn.query<{ Id: string; ShareType: string }>(`
      SELECT Id, ShareType
      FROM ContentDocumentLink
      WHERE ContentDocumentId = '${existingContentDocumentId}'
      AND LinkedEntityId = '${prSheetId}'
      LIMIT 1
    `)

    if (existingLinks.records && existingLinks.records.length > 0) {
      // 既存のリンクがSet by Record設定でない場合は更新
      if (existingLinks.records[0].ShareType !== 'I') {
        await conn.sobject('ContentDocumentLink').update({
          Id: existingLinks.records[0].Id,
          ShareType: 'I', // Inferred (Set by Record)
          Visibility: 'InternalUsers', // 全社員が閲覧可能
        })
      }
    } else {
      // リンクが存在しない場合は新規作成
      await conn.sobject('ContentDocumentLink').create({
        ContentDocumentId: existingContentDocumentId,
        LinkedEntityId: prSheetId,
        ShareType: 'I', // Inferred (Set by Record)
        Visibility: 'InternalUsers', // 全社員が閲覧可能
      })
    }
  }
  // 新規作成の場合も既存バージョンの場合も、FirstPublishLocationIdは指定しない
  // （手動でContentDocumentLinkを作成するため）

  const contentVersion = await conn.sobject(STANDARD_SOBJECT_NAMES.CONTENT_VERSION).create(contentVersionData)

  if (!contentVersion.success || !contentVersion.id) {
    throw new Error('PDFのアップロードに失敗しました')
  }

  // ContentDocumentIdを取得
  const cvResult = await conn.query<{ ContentDocumentId: string }>(`
    SELECT ContentDocumentId
    FROM ${STANDARD_SOBJECT_NAMES.CONTENT_VERSION}
    WHERE Id = '${contentVersion.id}'
    LIMIT 1
  `)

  if (!cvResult.records || cvResult.records.length === 0) {
    throw new Error('ContentDocumentIdの取得に失敗しました')
  }

  const contentDocumentId = cvResult.records[0].ContentDocumentId

  // 新規作成の場合、ContentDocumentLinkを明示的に作成
  // ShareType = "I" (Inferred) で作成することで「Set by Record」を実現
  if (!existingContentDocumentId) {
    await conn.sobject('ContentDocumentLink').create({
      ContentDocumentId: contentDocumentId,
      LinkedEntityId: prSheetId,
      ShareType: 'I', // Collaborator permission
      Visibility: 'InternalUsers', // 全社員が閲覧可能
    })
  }

  return contentDocumentId
}

// 画像アップロード (Salesforce Files / ContentVersion)
export async function uploadImage(
  conn: jsforce.Connection,
  fileName: string,
  base64Data: string,
  linkedEntityId?: string,
): Promise<string> {
  // ContentVersionレコード作成
  const contentVersion = await conn.sobject(STANDARD_SOBJECT_NAMES.CONTENT_VERSION).create({
    Title: fileName,
    PathOnClient: fileName,
    VersionData: base64Data,
    FirstPublishLocationId: linkedEntityId, // Career__cレコードに直接リンク
  })

  if (!contentVersion.success || !contentVersion.id) {
    throw new Error('画像アップロードに失敗しました')
  }

  // ContentDocumentIdを取得
  const cvResult = await conn.query<{ ContentDocumentId: string }>(`
    SELECT ContentDocumentId
    FROM ${STANDARD_SOBJECT_NAMES.CONTENT_VERSION}
    WHERE Id = '${contentVersion.id}'
    LIMIT 1
  `)

  if (!cvResult.records || cvResult.records.length === 0) {
    throw new Error('ContentDocumentIdの取得に失敗しました')
  }

  const contentDocumentId = cvResult.records[0].ContentDocumentId

  // ContentDocumentLinkを作成（InternalUsersに共有）
  // FirstPublishLocationIdを指定した場合は自動作成されるが、Visibilityを明示的に設定
  if (linkedEntityId) {
    // 既存のContentDocumentLinkを確認
    const existingLinks = await conn.query<{ Id: string }>(`
      SELECT Id
      FROM ContentDocumentLink
      WHERE ContentDocumentId = '${contentDocumentId}'
      AND LinkedEntityId = '${linkedEntityId}'
      LIMIT 1
    `)

    if (existingLinks.records && existingLinks.records.length > 0) {
      // 既存のリンクを更新
      await conn.sobject('ContentDocumentLink').update({
        Id: existingLinks.records[0].Id,
        ShareType: 'I', // Collaborator permission
        Visibility: 'InternalUsers', // 全社員が閲覧可能
      })
    } else {
      // 新規リンク作成
      await conn.sobject('ContentDocumentLink').create({
        ContentDocumentId: contentDocumentId,
        LinkedEntityId: linkedEntityId,
        ShareType: 'I', // Collaborator permission
        Visibility: 'InternalUsers', // 全社員が閲覧可能
      })
    }
  }

  return contentDocumentId
}

// 画像削除
export async function deleteImage(conn: jsforce.Connection, contentDocumentId: string): Promise<void> {
  await conn.sobject(STANDARD_SOBJECT_NAMES.CONTENT_DOCUMENT).delete(contentDocumentId)
}

// 画像URL取得（ダウンロードURL）
export function getImageUrl(instanceUrl: string, contentDocumentId: string): string {
  return `${instanceUrl}/sfc/servlet.shepherd/document/download/${contentDocumentId}`
}

// 画像のBase64データを取得（PDF生成用）
export async function getImageBase64(conn: jsforce.Connection, contentDocumentId: string): Promise<string> {
  // ContentDocumentIdからContentVersionIdを取得
  const cvResult = await conn.query<{ Id: string; VersionData: string }>(`
    SELECT Id, VersionData
    FROM ${STANDARD_SOBJECT_NAMES.CONTENT_VERSION}
    WHERE ContentDocumentId = '${contentDocumentId}'
    AND IsLatest = true
    LIMIT 1
  `)

  if (!cvResult.records || cvResult.records.length === 0) {
    throw new Error('ContentVersionが見つかりません')
  }

  const contentVersionId = cvResult.records[0].Id

  // REST APIでVersionDataを取得（Base64エンコード済み）
  const versionDataUrl = `${conn.instanceUrl}/services/data/v64.0/sobjects/ContentVersion/${contentVersionId}/VersionData`

  const response = await fetch(versionDataUrl, {
    headers: {
      Authorization: `Bearer ${conn.accessToken}`,
    },
  })

  if (!response.ok) {
    throw new Error('画像データの取得に失敗しました')
  }

  // BlobをBase64に変換
  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
